import { useEffect, useMemo } from 'react';

import { useQuery } from '@apollo/client/react';

import { gql } from '@graphql/__generated__';
import type { EmailTemplateSelectFragment } from '@graphql/__generated__/graphql';
import {
  getFullName,
  getPrimaryEmail,
  parseSuggestionsMap,
  toSuggestionLanguage,
} from '@modules/_shared/components/email-form/contextSuggestions';
import { EmailForm } from '@modules/_shared/components/email-form/EmailForm';
import type {
  ActivityEmailFormProps,
  EmailFallbackRecipient,
  EmailFormRecipient,
} from '@modules/_shared/components/email-form/types';
import { useActivityContext } from '@modules/_shared/components/feed/ActivityProvider';
import { DefaultActivityEmailForm } from '@modules/_shared/components/feed-v2/email/DefaultActivityEmailForm';
import { getEnquiryResponseEmailTemplate } from '@modules/_shared/components/listing-marketing/enquiryResponseEmailTemplate';
import { LoadingSpinner } from '@modules/_shared/components/LoadingSpinner';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { useLocale } from '@shared/hooks/locale';
import { toGlobalId } from '@shared/utils/global-id';

const GET_BUYER_LEAD_EMAIL_CONTEXT = gql(/* GraphQL */ `
  query GetBuyerLeadEmailContext($buyerLeadId: uuid!) {
    buyer_leads_by_pk(id: $buyerLeadId) {
      id
      user {
        id
        first_name
        last_name
        full_name
        emails(order_by: [{ primary: desc }, { verified: desc }], limit: 1) {
          email
        }
      }
      lot {
        id
        tenant_id
        title
        sale_price
        rent_net
        rent_extra
        brochure_doc_template_id
        lead {
          id
          cma_reports(limit: 1, order_by: { updated_at: desc }) {
            id
            doc_template_id
          }
        }
        response_sender {
          id
          first_name
          last_name
          title
          emails(order_by: [{ primary: desc }, { verified: desc }], limit: 1) {
            email
          }
          phone_numbers(
            order_by: [{ primary: desc }, { created_at: desc }]
            limit: 1
          ) {
            number
          }
          organisation {
            name
            street
            street_number
            postcode
            locality
            state
          }
        }
        broker {
          id
          first_name
          last_name
          title
          emails(order_by: [{ primary: desc }, { verified: desc }], limit: 1) {
            email
          }
          phone_numbers(
            order_by: [{ primary: desc }, { created_at: desc }]
            limit: 1
          ) {
            number
          }
          organisation {
            name
            street
            street_number
            postcode
            locality
            state
          }
        }
        property {
          id
          street_number
          route
          postcode
          locality
          state
          country_code
          number_of_rooms
          living_surface
          land_surface
          google_address {
            formatted_address
          }
          property_type {
            label
          }
          latest_appraisal {
            id
            min
            max
            value
          }
        }
        enquiryResponseTemplates: email_templates(
          where: { type: { _eq: "enquiry_auto_response" } }
          order_by: { updated_at: desc_nulls_last }
          limit: 1
        ) {
          ...EmailTemplateSelect
        }
        development {
          id
          enquiry_response_override_lot
          email_templates(
            where: { type: { _eq: "enquiry_auto_response" } }
            order_by: { updated_at: desc_nulls_last }
            limit: 1
          ) {
            ...EmailTemplateSelect
          }
        }
      }
    }
    enquiries(
      where: { buyer_lead_id: { _eq: $buyerLeadId } }
      order_by: { created_at: desc }
      limit: 1
    ) {
      id
      response_activity {
        id
      }
    }
  }
`);

const GET_BUYER_LEAD_SUGGESTIONS = gql(/* GraphQL */ `
  query GetBuyerLeadSuggestions(
    $senderId: uuid
    $recipients: [uuid!]
    $buyerLeadId: uuid
    $language: GetEmailTemplateSuggestionsLanguage
  ) {
    get_email_template_suggestions(
      senderId: $senderId
      recipients: $recipients
      buyerLeadId: $buyerLeadId
      language: $language
    ) {
      suggestions
    }
  }
`);

export const BuyerLeadActivityEmailForm = ({
  onClose,
  mode,
  replyToMessage,
  forwardMessage,
}: ActivityEmailFormProps) => {
  const { parent, reportError } = useActivityContext();
  const buyerLeadId = parent?.type === 'BuyerLead' ? parent.id : null;
  const { me } = useAppData();
  const { language } = useLocale();

  const { data, loading, error } = useQuery(GET_BUYER_LEAD_EMAIL_CONTEXT, {
    variables: { buyerLeadId: buyerLeadId ?? '' },
    skip: !buyerLeadId,
  });

  const buyerUserId = data?.buyer_leads_by_pk?.user?.id ?? null;

  const { data: suggestionsData, loading: suggestionsLoading } = useQuery(
    GET_BUYER_LEAD_SUGGESTIONS,
    {
      variables: {
        senderId: me?.id,
        recipients: buyerUserId != null ? [buyerUserId] : [],
        buyerLeadId,
        language: toSuggestionLanguage(language),
      },
      skip: buyerLeadId == null || me?.id == null || loading,
    },
  );

  const templateSuggestions = useMemo(
    () =>
      parseSuggestionsMap(
        suggestionsData?.get_email_template_suggestions?.suggestions,
      ),
    [suggestionsData],
  );

  useEffect(() => {
    if (error) {
      reportError(error);
    }
  }, [error, reportError]);

  const emailContext = useMemo(() => {
    const buyerLead = data?.buyer_leads_by_pk;
    const buyerUser = buyerLead?.user ?? null;
    const lot = buyerLead?.lot ?? null;
    const buyerEmail = getPrimaryEmail(buyerUser);
    const latestEnquiry = data?.enquiries[0] ?? null;
    const initialRecipients: EmailFormRecipient[] = [];
    const fallbackToRecipients: EmailFallbackRecipient[] = [];
    let initialTemplate: EmailTemplateSelectFragment | null = null;

    if (buyerUser?.id && buyerEmail) {
      initialRecipients.push({
        id: buyerUser.id,
        email: buyerEmail,
        name: getFullName(buyerUser) || undefined,
      });
    }

    if (buyerEmail) {
      fallbackToRecipients.push({
        email: buyerEmail,
        name: getFullName(buyerUser) || undefined,
      });
    }

    if (latestEnquiry != null && latestEnquiry.response_activity == null) {
      initialTemplate =
        getEnquiryResponseEmailTemplate({
          development: lot?.development,
          listingTemplates: lot?.enquiryResponseTemplates,
        }) ?? null;
    }

    return {
      parentGlobalId: buyerLeadId ? toGlobalId('BuyerLead', buyerLeadId) : null,
      lotId: lot?.id ?? null,
      initialRecipients,
      fallbackToRecipients,
      initialTemplate,
    };
  }, [buyerLeadId, data]);

  if (!buyerLeadId) {
    return (
      <DefaultActivityEmailForm
        onClose={onClose}
        mode={mode}
        replyToMessage={replyToMessage}
        forwardMessage={forwardMessage}
      />
    );
  }

  if ((loading && !data) || (suggestionsLoading && !suggestionsData)) {
    return <LoadingSpinner sx={{ flexGrow: 0 }} />;
  }

  return (
    <EmailForm
      onClose={onClose}
      parentGlobalId={emailContext.parentGlobalId}
      initialRecipients={emailContext.initialRecipients}
      fallbackToRecipients={emailContext.fallbackToRecipients}
      templateSuggestions={templateSuggestions}
      initialTemplate={emailContext.initialTemplate}
      snippetLotId={emailContext.lotId}
      mode={mode}
      replyToMessage={replyToMessage}
      forwardMessage={forwardMessage}
    />
  );
};
